import React, { useState, useEffect, useRef } from 'react';
import { Bell, Check, Calendar, Car, Clock, X } from 'lucide-react';
import { Button } from './ui/button';
import { apiFetch } from '../utils/api';


const ICON_MAP = {
  calendar: Calendar,
  car: Car,
  clock: Clock,
};

export default function NotificationBell() {
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 15000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (open) fetchNotifications();
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const res = await apiFetch(`/api/notifications/unread-count`);
      if (res.ok) {
        const data = await res.json();
        setUnreadCount(data.count || 0);
      }
    } catch {}
  };

  const fetchNotifications = async () => {
    try {
      const res = await apiFetch(`/api/notifications/history?limit=20`);
      if (res.ok) setNotifications(await res.json());
    } catch {}
  };

  const markRead = async (id) => {
    await apiFetch(`/api/notifications/${id}/read`, { method: 'PUT' });
    setNotifications(prev => prev.map(n => n.notification_id === id ? { ...n, read: true } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const markAllRead = async () => {
    await apiFetch(`/api/notifications/read-all`, { method: 'PUT' });
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  };

  const formatTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return 'Τώρα';
    if (diff < 3600) return `${Math.floor(diff / 60)} λεπ.`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} ώρ.`;
    return d.toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit' });
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-full hover:bg-white/20 transition-colors"
        data-testid="notification-bell"
      >
        <Bell size={22} className="text-white" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1" data-testid="notification-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-12 w-[calc(100vw-1rem)] max-w-sm sm:w-80 bg-white rounded-xl shadow-2xl border border-slate-200 z-50 overflow-hidden" data-testid="notification-panel">
          <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b">
            <h3 className="font-bold text-slate-800 text-sm">Ειδοποιήσεις</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button onClick={markAllRead} className="text-xs text-blue-600 hover:underline" data-testid="mark-all-read">
                  Ανάγνωση όλων
                </button>
              )}
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600">
                <X size={16} />
              </button>
            </div>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-6 text-center text-slate-400 text-sm">
                Δεν υπάρχουν ειδοποιήσεις
              </div>
            ) : (
              notifications.map((n) => {
                const IconComp = ICON_MAP[n.icon] || Bell;
                return (
                  <div
                    key={n.notification_id}
                    className={`flex items-start gap-3 px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors cursor-pointer ${!n.read ? 'bg-blue-50/50' : ''}`}
                    onClick={() => !n.read && markRead(n.notification_id)}
                    data-testid={`notification-item-${n.notification_id}`}
                  >
                    <div className={`mt-0.5 p-1.5 rounded-full ${!n.read ? 'bg-[#004C98]/10' : 'bg-slate-100'}`}>
                      <IconComp size={14} className={!n.read ? 'text-[#004C98]' : 'text-slate-400'} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className={`text-sm font-medium ${!n.read ? 'text-slate-900' : 'text-slate-500'}`}>{n.title}</span>
                        <span className="text-[10px] text-slate-400 ml-2 shrink-0">{formatTime(n.created_at)}</span>
                      </div>
                      <p className="text-xs text-slate-500 truncate">{n.body}</p>
                    </div>
                    {!n.read && <span className="w-2 h-2 rounded-full bg-[#004C98] mt-2 shrink-0" />}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
